import { Link } from 'react-router-dom'
import { supportsAttachments, type HomeworkSubmission } from '../services/homework'
import HomeworkHeader from './HomeworkHeader'
import HomeworkChat from './HomeworkChat'
import HomeworkAttachmentList from './HomeworkAttachmentList'
import submissionIconMark from '../assets/icon/submission-icon-mark.svg'
import crownIconMark from '../assets/icon/crown-icon-mark.svg'

interface HomeworkFeedbackViewProps {
  submission: HomeworkSubmission
}

// Presentational feedback view. The parent supplies the (homework-joined) submission;
// this component only renders. Grade & feedback stay blank ("—" / a pending message)
// until the submission is graded and — for school/entre — teacher-reviewed.
const HomeworkFeedbackView = ({ submission }: HomeworkFeedbackViewProps) => {
  // Grade/feedback visibility gate. School & entre submissions stay hidden until a
  // teacher/admin has reviewed them; everything else is visible once graded.
  const type = submission.homework?.type
  const reviewedGate = (type === 'school' || type === 'entre') && !submission.teacher_reviewed
  const gradeVisible = submission.status === 'graded' && !reviewedGate

  let pendingMessage = ''
  if (type === 'school' && !submission.teacher_reviewed) {
    pendingMessage = '先生が確認中です。評価はもうしばらくお待ちください。'
  } else if (type === 'entre' && !submission.teacher_reviewed) {
    pendingMessage = '管理者が確認中です。評価はもうしばらくお待ちください。'
  } else if (submission.status !== 'graded') {
    pendingMessage = '採点中です。しばらくお待ちください。'
  }

  return (
    <div className="min-h-screen">
      {/* Everything sits in one white panel; the gradient only frames it */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6">
        <div className="bg-white rounded-2xl shadow-lg px-4 py-5 sm:px-6 sm:py-6">
          {/* Header — same title/details block as the homework detail page */}
          {submission.homework && <HomeworkHeader homework={submission.homework} onWhite />}

          {/* Main two-column layout */}
          <div className="flex flex-col lg:flex-row gap-4">

            {/* Left: submitted answer + grade & feedback */}
            <div className="flex-1 flex flex-col">
              {/* Section header — dark text now that the panel behind it is white */}
              <div className="px-1 py-3 flex items-center gap-2">
                <img src={submissionIconMark} alt="" style={{ width: 45, height: 40 }} className="flex-shrink-0" />
                <span className="font-bold text-gray-900 text-base">提出した宿題の内容</span>
              </div>

              {/* The submitted answer — outlined instead of shadowed, it's white on white */}
              <div className="border border-gray-200 rounded-xl px-6 py-5">
                {/* An attachment-only submission carries no text answer. */}
                {submission.submitted_answer ? (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {submission.submitted_answer}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 leading-relaxed">
                    添付ファイルで提出しました。
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-4">
                  提出日時: {new Date(submission.submitted_at).toLocaleString('ja-JP')}
                </p>
                {type && supportsAttachments(type) && (
                  <div className="mt-4">
                    <HomeworkAttachmentList studentId={submission.student_id} homeworkId={submission.homework_id} />
                  </div>
                )}
              </div>

              {/* Grade + feedback, side by side below the submitted content */}
              <div className="flex gap-2 mt-4">
                {/* Grade */}
                <div className="w-32 flex-shrink-0 bg-[#da4574] rounded-lg flex flex-col items-center gap-4 p-4">
                  <div className="flex flex-col items-center gap-2">
                    <img src={crownIconMark} alt="" style={{ width: 45, height: 40 }} className="flex-shrink-0" />
                    <span className="text-white text-sm font-bold">評価</span>
                  </div>
                  <div className="w-full bg-white rounded-sm flex items-center justify-center py-2">
                    <span className="text-3xl text-[#da4574] font-medium leading-none">
                      {gradeVisible && submission.grade ? submission.grade : '—'}
                    </span>
                  </div>
                </div>

                {/* Feedback */}
                <div className="flex-1 flex flex-col gap-2">
                  <span className="text-sm font-bold text-[#da4574]">フィードバック</span>
                  <div className="flex-1 border border-gray-200 rounded-lg px-6 py-4">
                    {gradeVisible && submission.feedback_text ? (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {submission.feedback_text}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 leading-relaxed">
                        {pendingMessage || 'フィードバックはまだありません。'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Related video */}
              {submission.homework?.related_video_id && (
                <div className="bg-[#da4574]/5 border border-[#da4574]/20 rounded-xl p-4 mt-4">
                  <p className="text-sm font-semibold text-[#da4574] mb-2">関連動画も見てみよう</p>
                  <Link
                    to={`/video/${submission.homework.related_video_id}`}
                    className="inline-flex items-center gap-1 text-sm text-[#da4574] hover:underline"
                  >
                    動画を見る
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Link>
                </div>
              )}
            </div>

            {/* Right: AI chat — the same conversation as the homework detail page. No top
                padding, so its section header lines up with 提出した宿題の内容, and on lg it
                stretches to the left column's height so both cards end on the same line.
                Absolute on lg so a long conversation scrolls instead of stretching the row. */}
            {submission.homework && (
              <div className="lg:w-80 xl:w-96 flex-shrink-0 flex flex-col min-h-0 h-[60vh] lg:h-auto lg:relative">
                <div className="flex-1 min-h-0 flex flex-col lg:absolute lg:inset-0">
                  <HomeworkChat homework={submission.homework} studentId={submission.student_id} onWhite />
                </div>
              </div>
            )}

          </div>

          {/* Back button — under both columns so it doesn't lengthen the left one */}
          <div className="flex justify-center mt-8">
            <Link
              to="/homework"
              className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
              宿題トップへ戻る
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomeworkFeedbackView
